import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { newInputEditorTourSteps } from "./newInputEditorTour";
import { LeftNavigationSidebar, Room, AdjacencyPair, FixedRoom } from "./components/LeftNavigationSidebar";
import { TopToolbar, DrawingTool } from "./components/TopToolbar";
import { ZoomControls } from "./components/ZoomControls";
import { FloorplanPreviewModal } from "./components/FloorplanPreviewModal";
import { Canvas } from "./components/Canvas";
import { FixedRoomDetailsDialog } from "./components/FixedRoomDetailsDialog";

import { FixedRoomGeometry, useCanvasLogic } from "./hooks/useCanvasLogic";
import { convertLinesToBoundary, isClosedRectilinearBoundary } from "./utils/geometry";
import { DEFAULT_NEW_INPUT_DOCUMENT_TITLE, buildNewInputDocumentPayload, fetchSpaceOptimization, fetchTaskResult, fetchGAOptimization, createNewInputDocument, updateNewInputDocument, EditorStateSnapshot, NewInputDocumentPayload } from "./services/api";
import { convertSpaceResultToGARequest } from "./utils/api_translation";
import { AppDispatch } from "../../redux/store";
import {
    resetStates,
    setCleanUpDone,
    setCleanUpRequired,
    setHeight,
    setLabels,
    setRooms as setRoomsState,
    setWalls,
    setWidth,
    setPlot,
    setShowPlot,
} from "../../redux/features/appState";
import { reset, setFromNewInputEditor } from "../../redux/features/graph/graph.slice";
import { showToast } from "../../redux/features/toast/toast.slice";

const DISTINCT_COLORS = [
    "#FFB3B3", 
    "#B3D9FF", 
    "#B3FFCC", 
    "#FFE6B3", 
    "#E6B3FF", 
    "#FFB3E6", 
    "#B3FFF0", 
    "#FFF0B3", 
    "#C4B3FF", 
    "#FFC4B3", 
    "#B3FFE6", 
    "#E6FFB3"  
];

const getValidColor = (c?: string, index: number = 0): string => {
    if (!c) return DISTINCT_COLORS[index % DISTINCT_COLORS.length];
    const lower = c.trim().toLowerCase();
    if (lower === "#000" || lower === "#000000" || lower === "black" || lower === "rgb(0,0,0)" || lower === "rgba(0,0,0,1)" || lower.length < 4 || lower === "#fff7ec") {
        return DISTINCT_COLORS[index % DISTINCT_COLORS.length];
    }
    if (lower.startsWith("#")) {
        const hex = lower.slice(1);
        if (hex.length === 3) {
            const r = parseInt(hex[0], 16) * 17;
            const g = parseInt(hex[1], 16) * 17;
            const b = parseInt(hex[2], 16) * 17;
            if (r < 60 && g < 60 && b < 60) return DISTINCT_COLORS[index % DISTINCT_COLORS.length];
        } else if (hex.length === 6) {
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            if (r < 60 && g < 60 && b < 60) return DISTINCT_COLORS[index % DISTINCT_COLORS.length];
        }
    }
    return c;
};

/* Main Page Layout using Flex and Floating Panels */
const PageContainer = styled.div`
    width: 100vw;
    height: 100vh;
    background: #000000; /* Black background */
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    box-sizing: border-box;
    gap: 24px;
`;

/* Floating Sidebar Wrapper */
const SidebarWrapper = styled.div`
    width: 320px;
    height: 100%;
    flex-shrink: 0;
`;

/* Main Right Panel (Light Peach) */
const MainPanel = styled.div`
    flex: 1;
    height: 100%;
    background: #FBE5C6;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    padding: 24px;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;
`;

const SectionTitle = styled.h2`
    font-family: Poppins, sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 16px 8px;
`;

/* Canvas Container: White/Blue rect inside Main Panel */
const CanvasFrame = styled.div`
    flex: 1;
    background: #F0F8FF;
    border: 2px solid #1a1a1a;
    border-radius: 12px;
    position: relative;
    overflow: hidden;
    margin-top: 10px;
    box-shadow: inset 0 0 10px rgba(0,0,0,0.05);
`;

const SESSION_KEY = "newInputEditorState";

const isPointOnSegment = ([px, py]: [number, number], [x1, y1]: [number, number], [x2, y2]: [number, number]) => {
    const cross = (py - y1) * (x2 - x1) - (px - x1) * (y2 - y1);
    if (Math.abs(cross) > 1e-6) return false;

    const dot = (px - x1) * (px - x2) + (py - y1) * (py - y2);
    return dot <= 1e-6;
};

const isPointInsidePolygon = (point: [number, number], polygon: [number, number][]) => {
    if (polygon.length < 3) return false;

    for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        if (isPointOnSegment(point, a, b)) return true;
    }

    let inside = false;
    const [x, y] = point;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];

        const intersects = ((yi > y) !== (yj > y))
            && (x < ((xj - xi) * (y - yi)) / (yj - yi || Number.EPSILON) + xi);

        if (intersects) inside = !inside;
    }

    return inside;
};

const isFixedRoomInsideBoundary = (
    fixedRoom: Pick<FixedRoom, "x" | "y" | "width" | "height">,
    boundary: [number, number][],
) => {
    const corners: [number, number][] = [
        [fixedRoom.x, fixedRoom.y],
        [fixedRoom.x + fixedRoom.width, fixedRoom.y],
        [fixedRoom.x + fixedRoom.width, fixedRoom.y + fixedRoom.height],
        [fixedRoom.x, fixedRoom.y + fixedRoom.height],
    ];

    return corners.every((corner) => isPointInsidePolygon(corner, boundary));
};

const extractErrorMessage = (error: any, fallback: string) => {
    if (typeof error === "string" && error.trim()) return error;
    if (typeof error?.response?.data?.error?.message === "string") return error.response.data.error.message;
    if (typeof error?.response?.data?.message === "string") return error.response.data.message;
    if (typeof error?.response?.data?.detail === "string") return error.response.data.detail;
    if (typeof error?.data?.error?.message === "string") return error.data.error.message;
    if (typeof error?.data?.message === "string") return error.data.message;
    if (typeof error?.data?.detail === "string") return error.data.detail;
    if (typeof error?.error?.message === "string") return error.error.message;
    if (typeof error?.error === "string") return error.error;
    if (typeof error?.message === "string") return error.message;

    const objectCandidate =
        error?.response?.data?.error ||
        error?.response?.data ||
        error?.data?.error ||
        error?.data ||
        error?.error ||
        error;

    if (objectCandidate && typeof objectCandidate === "object") {
        try {
            const asJson = JSON.stringify(objectCandidate);
            if (asJson && asJson !== "{}") return asJson;
        } catch {
            // Ignore JSON stringify errors and return fallback below.
        }
    }

    return fallback;
};

const hasMeaningfulError = (error: any) => {
    if (error == null) return false;
    if (typeof error === "string") return error.trim().length > 0;
    if (typeof error === "object") return Object.keys(error).length > 0;
    return Boolean(error);
};

const GEOMETRY_EPS = 1e-6;
const TRAILING_NUMBER_SUFFIX_REGEX = /-\d+$/;

const getEntityNameBase = (name: string) => name.trim().replace(TRAILING_NUMBER_SUFFIX_REGEX, "").trim();

const enforceUniqueEntityNames = <T extends { name: string }>(items: T[]): T[] => {
    const groupedCounts = new Map<string, number>();
    const forceNumbering = new Map<string, boolean>();

    items.forEach((item) => {
        const trimmed = item.name.trim();
        if (!trimmed) return;

        const baseName = getEntityNameBase(trimmed);
        if (!baseName) return;

        const key = baseName.toLowerCase();
        groupedCounts.set(key, (groupedCounts.get(key) || 0) + 1);

        if (TRAILING_NUMBER_SUFFIX_REGEX.test(trimmed)) {
            forceNumbering.set(key, true);
        }
    });

    const groupedIndices = new Map<string, number>();

    return items.map((item) => {
        const trimmed = item.name.trim();
        if (!trimmed) {
            if (item.name === "") return item;
            return { ...item, name: "" };
        }

        const baseName = getEntityNameBase(trimmed);
        const key = baseName.toLowerCase();
        const duplicateCount = groupedCounts.get(key) || 0;
        const shouldNumber = duplicateCount > 1 || Boolean(forceNumbering.get(key));

        let nextName = trimmed;
        if (shouldNumber) {
            const nextIndex = (groupedIndices.get(key) || 0) + 1;
            groupedIndices.set(key, nextIndex);
            nextName = `${baseName}-${nextIndex}`;
        }

        if (nextName === item.name) return item;
        return { ...item, name: nextName };
    });
};

type FixedRoomRequest = {
    name: string;
    x: number;
    y: number;
    width: number;
    height: number;
    is_fixed?: boolean;
    max_expansion?: number;
};

type PlacementRect = {
    name: string;
    x: number;
    y: number;
    width: number;
    height: number;
    isFixed: boolean;
};

type FixedProjection = "translate" | "flip-x" | "flip-y" | "rotate-180";

type LayoutValidationResult = {
    isValid: boolean;
    issues: string[];
};

type RegionRect = {
    x: number;
    y: number;
    width: number;
    height: number;
};

const getRegionBounds = (regions: any[]) => {
    if (!Array.isArray(regions) || regions.length === 0) return null;

    const parsed = regions
        .map((region) => ({
            x: Number(region?.x),
            y: Number(region?.y),
            width: Number(region?.width),
            height: Number(region?.height),
        }))
        .filter((region) => Number.isFinite(region.x) && Number.isFinite(region.y) && Number.isFinite(region.width) && Number.isFinite(region.height));

    if (parsed.length === 0) return null;

    const minX = Math.min(...parsed.map((region) => region.x));
    const minY = Math.min(...parsed.map((region) => region.y));
    const maxX = Math.max(...parsed.map((region) => region.x + region.width));
    const maxY = Math.max(...parsed.map((region) => region.y + region.height));

    return {
        minX,
        minY,
        maxX,
        maxY,
        width: Math.max(0, maxX - minX),
        height: Math.max(0, maxY - minY),
    };
};

const getBoundaryBounds = (boundary: [number, number][]) => {
    if (!Array.isArray(boundary) || boundary.length === 0) return null;

    const parsed = boundary
        .map((point) => ({
            x: Number(point?.[0]),
            y: Number(point?.[1]),
        }))
        .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

    if (parsed.length === 0) return null;

    const minX = Math.min(...parsed.map((point) => point.x));
    const minY = Math.min(...parsed.map((point) => point.y));
    const maxX = Math.max(...parsed.map((point) => point.x));
    const maxY = Math.max(...parsed.map((point) => point.y));

    return {
        minX,
        minY,
        maxX,
        maxY,
        width: Math.max(0, maxX - minX),
        height: Math.max(0, maxY - minY),
    };
};

const toPlacementRect = (placement: any): PlacementRect | null => {
    const x = Number(placement?.x);
    const y = Number(placement?.y);
    const width = Number(placement?.width);
    const height = Number(placement?.height);

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
        return null;
    }

    if (width <= 0 || height <= 0) return null;

    return {
        name: String(placement?.name ?? ""),
        x,
        y,
        width,
        height,
        isFixed: Boolean(placement?.is_fixed),
    };
};

const isRectInsideBounds = (
    rect: Pick<PlacementRect, "x" | "y" | "width" | "height">,
    bounds: NonNullable<ReturnType<typeof getRegionBounds>>,
) => (
    rect.x >= bounds.minX - GEOMETRY_EPS
    && rect.y >= bounds.minY - GEOMETRY_EPS
    && rect.x + rect.width <= bounds.maxX + GEOMETRY_EPS
    && rect.y + rect.height <= bounds.maxY + GEOMETRY_EPS
);

const getRectOverlapArea = (
    a: Pick<PlacementRect, "x" | "y" | "width" | "height">,
    b: Pick<PlacementRect, "x" | "y" | "width" | "height">,
) => {
    const overlapLeft = Math.max(a.x, b.x);
    const overlapRight = Math.min(a.x + a.width, b.x + b.width);
    const overlapTop = Math.max(a.y, b.y);
    const overlapBottom = Math.min(a.y + a.height, b.y + b.height);

    if (overlapRight <= overlapLeft || overlapBottom <= overlapTop) return 0;
    return (overlapRight - overlapLeft) * (overlapBottom - overlapTop);
};

const toRegionRect = (region: any): RegionRect | null => {
    const x = Number(region?.x);
    const y = Number(region?.y);
    const width = Number(region?.width);
    const height = Number(region?.height);

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
        return null;
    }

    if (width <= 0 || height <= 0) return null;

    return { x, y, width, height };
};

const isPointInsideRegionRect = (point: [number, number], region: RegionRect) => {
    const [x, y] = point;
    return (
        x >= region.x - GEOMETRY_EPS
        && x <= region.x + region.width + GEOMETRY_EPS
        && y >= region.y - GEOMETRY_EPS
        && y <= region.y + region.height + GEOMETRY_EPS
    );
};

const isPointInsideAnyRegion = (point: [number, number], regions: RegionRect[]) => {
    for (const region of regions) {
        if (isPointInsideRegionRect(point, region)) return true;
    }
    return false;
};

const getPolygonArea = (polygon: [number, number][]) => {
    if (!Array.isArray(polygon) || polygon.length < 3) return 0;

    const twiceArea = polygon.reduce((acc, [x1, y1], index) => {
        const [x2, y2] = polygon[(index + 1) % polygon.length];
        return acc + x1 * y2 - x2 * y1;
    }, 0);

    return Math.abs(twiceArea) / 2;
};

const getCombinedBounds = (
    a: NonNullable<ReturnType<typeof getBoundaryBounds>>,
    b: NonNullable<ReturnType<typeof getRegionBounds>>,
) => ({
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
    width: Math.max(a.maxX, b.maxX) - Math.min(a.minX, b.minX),
    height: Math.max(a.maxY, b.maxY) - Math.min(a.minY, b.minY),
});

const getAdaptiveSampleStep = (bounds: { width: number; height: number }) => {
    const area = Math.max(1, bounds.width * bounds.height);
    if (area <= 40_000) return 1;
    if (area <= 160_000) return 2;
    return Math.max(1, Math.ceil(Math.sqrt(area / 40_000)));
};

const transformRectBetweenBounds = (
    rect: Pick<RegionRect, "x" | "y" | "width" | "height">,
    sourceBounds: NonNullable<ReturnType<typeof getRegionBounds>>,
    targetBounds: NonNullable<ReturnType<typeof getBoundaryBounds>>,
    projection: FixedProjection,
): RegionRect => {
    const relX = rect.x - sourceBounds.minX;
    const relY = rect.y - sourceBounds.minY;

    const shouldFlipX = projection === "flip-x" || projection === "rotate-180";
    const shouldFlipY = projection === "flip-y" || projection === "rotate-180";

    const x = shouldFlipX
        ? targetBounds.minX + (targetBounds.width - (relX + rect.width))
        : targetBounds.minX + relX;

    const y = shouldFlipY
        ? targetBounds.minY + (targetBounds.height - (relY + rect.height))
        : targetBounds.minY + relY;

    return {
        x,
        y,
        width: rect.width,
        height: rect.height,
    };
};

const transformPlacementBetweenBounds = (
    placement: any,
    sourceBounds: NonNullable<ReturnType<typeof getRegionBounds>>,
    targetBounds: NonNullable<ReturnType<typeof getBoundaryBounds>>,
    projection: FixedProjection,
) => {
    const rect = toPlacementRect(placement);
    if (!rect) return placement;

    const transformed = transformRectBetweenBounds(rect, sourceBounds, targetBounds, projection);
    return {
        ...placement,
        x: transformed.x,
        y: transformed.y,
        width: transformed.width,
        height: transformed.height,
    };
};

const getRegionShapeMismatchScore = (
    requestBoundary: [number, number][],
    transformedRegions: RegionRect[],
) => {
    const boundaryBounds = getBoundaryBounds(requestBoundary);
    const regionBounds = getRegionBounds(transformedRegions);

    if (!boundaryBounds || !regionBounds || transformedRegions.length === 0) {
        return Number.MAX_SAFE_INTEGER;
    }

    const combinedBounds = getCombinedBounds(boundaryBounds, regionBounds);
    const sampleStep = getAdaptiveSampleStep(combinedBounds);

    let mismatchCount = 0;
    let sampleCount = 0;

    for (let x = combinedBounds.minX + sampleStep / 2; x < combinedBounds.maxX - GEOMETRY_EPS; x += sampleStep) {
        for (let y = combinedBounds.minY + sampleStep / 2; y < combinedBounds.maxY - GEOMETRY_EPS; y += sampleStep) {
            const insideBoundary = isPointInsidePolygon([x, y], requestBoundary);
            const insideRegions = isPointInsideAnyRegion([x, y], transformedRegions);
            if (insideBoundary !== insideRegions) mismatchCount += 1;
            sampleCount += 1;
        }
    }

    const mismatchArea = mismatchCount * sampleStep * sampleStep;
    const boundaryArea = getPolygonArea(requestBoundary);
    const regionArea = transformedRegions.reduce((sum, region) => sum + region.width * region.height, 0);
    const areaDelta = Math.abs(boundaryArea - regionArea);
    const emptyPenalty = sampleCount === 0 ? Number.MAX_SAFE_INTEGER : 0;

    return mismatchArea + areaDelta + emptyPenalty;
};

const getFixedAnchorMismatchScore = (
    placements: any[],
    requestFixedRooms: FixedRoomRequest[],
) => {
    if (requestFixedRooms.length === 0) return 0;

    const placementsByName = new Map(
        placements
            .map((placement) => [String(placement?.name ?? ""), placement] as const)
            .filter(([name]) => name.length > 0),
    );

    let score = 0;

    requestFixedRooms.forEach((room) => {
        const placement = placementsByName.get(room.name);
        if (!placement) {
            score += 1_000_000;
            return;
        }

        const px = Number(placement?.x);
        const py = Number(placement?.y);
        const pw = Number(placement?.width);
        const ph = Number(placement?.height);

        if (![px, py, pw, ph].every(Number.isFinite)) {
            score += 500_000;
            return;
        }

        const xPenalty = Math.abs(px - room.x);
        const yPenalty = Math.abs(py - room.y);
        const wPenalty = Math.abs(pw - room.width);
        const hPenalty = Math.abs(ph - room.height);

        score += xPenalty * 100 + yPenalty * 100 + wPenalty * 300 + hPenalty * 300;
    });

    return score;
};

export const evaluatePlacementGeometry = (
    placements: any[],
    bounds: ReturnType<typeof getRegionBounds>,
) => {
    const rects: PlacementRect[] = [];
    let invalidCount = 0;

    placements.forEach((placement) => {
        const rect = toPlacementRect(placement);
        if (!rect) {
            invalidCount += 1;
            return;
        }
        rects.push(rect);
    });

    const outOfBoundsNames: string[] = [];
    if (bounds) {
        rects.forEach((rect) => {
            if (!isRectInsideBounds(rect, bounds)) {
                outOfBoundsNames.push(rect.name || "unnamed");
            }
        });
    }

    const overlapPairs: [string, string][] = [];
    let totalOverlapArea = 0;

    for (let i = 0; i < rects.length; i++) {
        for (let j = i + 1; j < rects.length; j++) {
            const overlapArea = getRectOverlapArea(rects[i], rects[j]);
            if (overlapArea > GEOMETRY_EPS) {
                totalOverlapArea += overlapArea;
                overlapPairs.push([
                    rects[i].name || `room-${i + 1}`,
                    rects[j].name || `room-${j + 1}`,
                ]);
            }
        }
    }

    return { rects, invalidCount, outOfBoundsNames, overlapPairs, totalOverlapArea };
};

const getFixedLayoutPenalty = (
    placements: any[],
    bounds: ReturnType<typeof getRegionBounds>,
    requestFixedRooms: FixedRoomRequest[],
) => {
    const fixedNameSet = new Set(requestFixedRooms.map((room) => room.name));
    const geometry = evaluatePlacementGeometry(placements, bounds);

    const presentFixedNames = new Set(
        geometry.rects
            .filter((rect) => fixedNameSet.has(rect.name))
            .map((rect) => rect.name),
    );

    const missingCount = requestFixedRooms.reduce(
        (acc, room) => acc + (presentFixedNames.has(room.name) ? 0 : 1),
        0,
    );

    let fixedOutOfBoundsCount = 0;
    if (bounds) {
        geometry.rects.forEach((rect) => {
            if (fixedNameSet.has(rect.name) && !isRectInsideBounds(rect, bounds)) {
                fixedOutOfBoundsCount += 1;
            }
        });
    }

    let fixedOverlapCount = 0;
    let fixedOverlapArea = 0;
    for (let i = 0; i < geometry.rects.length; i++) {
        for (let j = i + 1; j < geometry.rects.length; j++) {
            const rectA = geometry.rects[i];
            const rectB = geometry.rects[j];
            if (!fixedNameSet.has(rectA.name) && !fixedNameSet.has(rectB.name)) continue;

            const overlapArea = getRectOverlapArea(rectA, rectB);
            if (overlapArea > GEOMETRY_EPS) {
                fixedOverlapCount += 1;
                fixedOverlapArea += overlapArea;
            }
        }
    }

    const score = (
        missingCount * 1_000_000_000
        + fixedOutOfBoundsCount * 10_000_000
        + fixedOverlapCount * 1_000_000
        + fixedOverlapArea * 1_000
    );

    return {
        missingCount,
        fixedOutOfBoundsCount,
        fixedOverlapCount,
        fixedOverlapArea,
        score,
    };
};

const mapFixedRoomToRegionFrame = (
    room: FixedRoomRequest,
    requestBounds: NonNullable<ReturnType<typeof getBoundaryBounds>>,
    regionBounds: NonNullable<ReturnType<typeof getRegionBounds>>,
    projection: FixedProjection,
): FixedRoomRequest => {
    const translatedX = regionBounds.minX + (room.x - requestBounds.minX);
    const translatedY = regionBounds.minY + (room.y - requestBounds.minY);

    const mirroredX = regionBounds.minX + (requestBounds.maxX - (room.x + room.width));
    const mirroredY = regionBounds.minY + (requestBounds.maxY - (room.y + room.height));

    switch (projection) {
        case "flip-x":
            return { ...room, x: mirroredX, y: translatedY, is_fixed: true };
        case "flip-y":
            return { ...room, x: translatedX, y: mirroredY, is_fixed: true };
        case "rotate-180":
            return { ...room, x: mirroredX, y: mirroredY, is_fixed: true };
        case "translate":
        default:
            return { ...room, x: translatedX, y: translatedY, is_fixed: true };
    }
};

const applyProjectedFixedRooms = (placements: any[], projectedFixedRooms: FixedRoomRequest[]) => {
    const projectedByName = new Map(projectedFixedRooms.map((room) => [room.name, room]));
    const replacedNames = new Set<string>();

    const updatedPlacements = placements.map((placement) => {
        const name = String(placement?.name ?? "");
        const projected = projectedByName.get(name);

        if (!projected) return placement;

        replacedNames.add(name);
        return {
            ...placement,
            x: projected.x,
            y: projected.y,
            width: projected.width,
            height: projected.height,
            is_fixed: true,
            original_width: placement?.original_width ?? projected.width,
            original_height: placement?.original_height ?? projected.height,
            area: projected.width * projected.height,
        };
    });

    projectedFixedRooms.forEach((projected) => {
        if (replacedNames.has(projected.name)) return;
        updatedPlacements.push({
            name: projected.name,
            x: projected.x,
            y: projected.y,
            width: projected.width,
            height: projected.height,
            is_fixed: true,
            rotated: false,
            original_width: projected.width,
            original_height: projected.height,
            area: projected.width * projected.height,
        });
    });

    return updatedPlacements;
};

const normalizeFixedRoomsByOrientation = (
    placements: any[],
    requestBoundary: [number, number][],
    requestFixedRooms: FixedRoomRequest[],
    bounds: NonNullable<ReturnType<typeof getRegionBounds>>,
) => {
    if (requestFixedRooms.length === 0) return placements;

    const requestBounds = getBoundaryBounds(requestBoundary);
    if (!requestBounds) return placements;

    const currentGeometry = evaluatePlacementGeometry(placements, bounds);
    const currentPenalty = getFixedLayoutPenalty(placements, bounds, requestFixedRooms);

    const shouldAttemptNormalization =
        currentPenalty.missingCount > 0
        || currentPenalty.fixedOutOfBoundsCount > 0
        || currentPenalty.fixedOverlapCount > 0;

    if (!shouldAttemptNormalization) return placements;

    let bestPlacements = placements;
    let bestScore = (
        currentPenalty.score
        + currentGeometry.invalidCount * 100_000
        + currentGeometry.outOfBoundsNames.length * 10_000
        + currentGeometry.totalOverlapArea
    );

    const projections: FixedProjection[] = ["translate", "flip-x", "flip-y", "rotate-180"];

    projections.forEach((projection) => {
        const projectedFixedRooms = requestFixedRooms.map((room) =>
            mapFixedRoomToRegionFrame(room, requestBounds, bounds, projection),
        );

        const candidatePlacements = applyProjectedFixedRooms(placements, projectedFixedRooms);
        const candidateGeometry = evaluatePlacementGeometry(candidatePlacements, bounds);
        const candidatePenalty = getFixedLayoutPenalty(candidatePlacements, bounds, requestFixedRooms);

        const candidateScore = (
            candidatePenalty.score
            + candidateGeometry.invalidCount * 100_000
            + candidateGeometry.outOfBoundsNames.length * 10_000
            + candidateGeometry.totalOverlapArea
        );

        if (candidateScore + GEOMETRY_EPS < bestScore) {
            bestScore = candidateScore;
            bestPlacements = candidatePlacements;
        }
    });

    return bestPlacements;
};

const alignOutputWithRequestBoundary = (
    outputData: any,
    placementsInRegionFrame: any[],
    requestBoundary: [number, number][],
    requestFixedRooms: FixedRoomRequest[],
) => {
    const parsedRegions = (Array.isArray(outputData?.floor?.regions) ? outputData.floor.regions : [])
        .map(toRegionRect)
        .filter(Boolean) as RegionRect[];

    if (parsedRegions.length === 0) {
        return {
            placements: placementsInRegionFrame,
            regions: [] as RegionRect[],
        };
    }

    const sourceBounds = getRegionBounds(parsedRegions);
    const targetBounds = getBoundaryBounds(requestBoundary);

    if (!sourceBounds || !targetBounds) {
        return {
            placements: placementsInRegionFrame,
            regions: parsedRegions,
        };
    }

    const projections: FixedProjection[] = ["translate", "flip-x", "flip-y", "rotate-180"];

    let bestPlacements = placementsInRegionFrame;
    let bestRegions = parsedRegions;
    let bestScore = Number.POSITIVE_INFINITY;

    projections.forEach((projection) => {
        const transformedRegions = parsedRegions.map((region) =>
            transformRectBetweenBounds(region, sourceBounds, targetBounds, projection),
        );

        const transformedPlacements = placementsInRegionFrame.map((placement) =>
            transformPlacementBetweenBounds(placement, sourceBounds, targetBounds, projection),
        );

        // Keep fixed rooms anchored to user-requested coordinates across all projections.
        const anchoredPlacements = applyProjectedFixedRooms(
            transformedPlacements,
            requestFixedRooms.map((room) => ({ ...room, is_fixed: true })),
        );

        const transformedBounds = getRegionBounds(transformedRegions);
        const geometry = evaluatePlacementGeometry(anchoredPlacements, transformedBounds);
        const fixedPenalty = getFixedLayoutPenalty(anchoredPlacements, transformedBounds, requestFixedRooms);
        const boundaryMismatch = getRegionShapeMismatchScore(requestBoundary, transformedRegions);
        const fixedAnchorMismatch = getFixedAnchorMismatchScore(anchoredPlacements, requestFixedRooms);

        const candidateScore = (
            boundaryMismatch * 1_000_000
            + fixedAnchorMismatch * 100_000
            + fixedPenalty.score
            + geometry.invalidCount * 100_000_000
            + geometry.outOfBoundsNames.length * 10_000_000
            + geometry.overlapPairs.length * 1_000_000
            + geometry.totalOverlapArea * 10_000
        );

        if (candidateScore + GEOMETRY_EPS < bestScore) {
            bestScore = candidateScore;
            bestPlacements = anchoredPlacements;
            bestRegions = transformedRegions;
        }
    });

    return {
        placements: bestPlacements,
        regions: bestRegions,
    };
};

const normalizePlacementsWithBoundaryShift = (
    outputData: any,
    requestBoundary: [number, number][],
    requestFixedRooms: FixedRoomRequest[] = [],
) => {
    const placements = Array.isArray(outputData?.placements) ? outputData.placements : [];
    const regions = outputData?.floor?.regions;
    const bounds = getRegionBounds(regions);
    const boundaryBounds = getBoundaryBounds(requestBoundary);

    if (!bounds || !boundaryBounds || placements.length === 0) {
        return outputData;
    }

    const shiftX = boundaryBounds.minX - bounds.minX;
    const shiftY = boundaryBounds.minY - bounds.minY;

    const shiftedPlacements = placements.map((placement: any) => {
        const rect = toPlacementRect(placement);
        if (!rect) {
            return placement;
        }

        if (isRectInsideBounds(rect, bounds)) {
            return placement;
        }

        const shiftedRect = {
            ...rect,
            x: rect.x - shiftX,
            y: rect.y - shiftY,
        };

        if (isRectInsideBounds(shiftedRect, bounds)) {
            return {
                ...placement,
                x: shiftedRect.x,
                y: shiftedRect.y,
            };
        }

        return placement;
    });

    const orientationNormalizedPlacements = normalizeFixedRoomsByOrientation(
        shiftedPlacements,
        requestBoundary,
        requestFixedRooms,
        bounds,
    );

    const alignedOutput = alignOutputWithRequestBoundary(
        outputData,
        orientationNormalizedPlacements,
        requestBoundary,
        requestFixedRooms,
    );

    const alignedBounds = getRegionBounds(alignedOutput.regions);

    return {
        ...outputData,
        floor: outputData?.floor
            ? {
                ...outputData.floor,
                ...(alignedOutput.regions.length > 0 ? { regions: alignedOutput.regions } : {}),
                ...(alignedBounds ? { width: alignedBounds.width, height: alignedBounds.height } : {}),
            }
            : outputData?.floor,
        placements: alignedOutput.placements,
    };
};

const validateNormalizedFloorplan = (
    outputData: any,
    requestFixedRooms: FixedRoomRequest[] = [],
    requestBoundary: [number, number][] = [],
): LayoutValidationResult => {
    const issues: string[] = [];

    const placements = Array.isArray(outputData?.placements) ? outputData.placements : [];
    const bounds = getRegionBounds(outputData?.floor?.regions);

    if (!bounds) {
        issues.push("Missing or invalid floor regions in API response");
    }

    if (placements.length === 0) {
        issues.push("No placements returned by API");
    }

    const geometry = evaluatePlacementGeometry(placements, bounds);

    if (geometry.invalidCount > 0) {
        issues.push(`${geometry.invalidCount} placement(s) have invalid coordinates or dimensions`);
    }

    if (geometry.outOfBoundsNames.length > 0) {
        const sample = Array.from(new Set(geometry.outOfBoundsNames)).slice(0, 3).join(", ");
        issues.push(`Out-of-bounds rooms: ${sample}`);
    }

    if (geometry.overlapPairs.length > 0) {
        const sample = geometry.overlapPairs.slice(0, 3).map(([a, b]) => `${a}/${b}`).join(", ");
        issues.push(`Overlapping rooms detected: ${sample}`);
    }

    if (Array.isArray(requestBoundary) && requestBoundary.length > 2) {
        const outsideBoundary = geometry.rects.filter((rect) =>
            !isFixedRoomInsideBoundary(
                {
                    x: rect.x,
                    y: rect.y,
                    width: rect.width,
                    height: rect.height,
                },
                requestBoundary,
            ),
        );

        if (outsideBoundary.length > 0) {
            const sample = outsideBoundary.slice(0, 3).map((rect) => rect.name || "unnamed").join(", ");
            issues.push(`Outside request boundary: ${sample}`);
        }
    }

    if (requestFixedRooms.length > 0) {
        const fixedPenalty = getFixedLayoutPenalty(placements, bounds, requestFixedRooms);

        if (fixedPenalty.missingCount > 0) {
            issues.push(`Missing ${fixedPenalty.missingCount} fixed room(s) in API output`);
        }

        if (fixedPenalty.fixedOutOfBoundsCount > 0) {
            issues.push(`${fixedPenalty.fixedOutOfBoundsCount} fixed room(s) are out of bounds`);
        }

        if (fixedPenalty.fixedOverlapCount > 0) {
            issues.push(`Fixed room overlap count: ${fixedPenalty.fixedOverlapCount}`);
        }
    }

    return {
        isValid: issues.length === 0,
        issues,
    };
};

const BoundaryEditorPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();

    const [gridSpacing, setGridSpacing] = useState(2);
    const [activeSection, setActiveSection] = useState<"boundary" | "rooms" | "adjacency" | "nonadjacency">("boundary");
    const [activeTool, setActiveTool] = useState<DrawingTool>(null);

    const [rooms, setRooms] = useState<Room[]>([]);
    const [fixedRooms, setFixedRooms] = useState<FixedRoom[]>([]);
    const [pendingFixedRoom, setPendingFixedRoom] = useState<FixedRoomGeometry | null>(null);
    const [fixedRoomDialogOpen, setFixedRoomDialogOpen] = useState(false);
    const [adjacencyPairs, setAdjacencyPairs] = useState<AdjacencyPair[]>([]);
    const [nonAdjacencyPairs, setNonAdjacencyPairs] = useState<AdjacencyPair[]>([]);

    const { refs, state, actions } = useCanvasLogic(gridSpacing, {
        fixedRoomMode: activeTool === "fixedspace",
        fixedRooms,
        pendingFixedRoom,
        onFixedRoomDrawn: (rect) => {
            setPendingFixedRoom(rect);
            setFixedRoomDialogOpen(true);
            setActiveSection("rooms");
        },
    });

    const hasValidBoundary = useMemo(() => {
        const revisionTick = state.revision;
        void revisionTick;
        return isClosedRectilinearBoundary(state.linesRef.current);
    }, [state.linesRef, state.revision]);

    const [loading, setLoading] = useState(false);
    const [floorplans, setFloorplans] = useState<any[]>([]);
    const [currentFloorplanIndex, setCurrentFloorplanIndex] = useState(0);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [autoExpand, setAutoExpand] = useState(false);

    // GA Circulation state
    const [spaceResult, setSpaceResult] = useState<any>(null);
    const [gaResult, setGAResult] = useState<any>(null);
    const [isGALoading, setIsGALoading] = useState(false);
    const [idToNameMapState, setIdToNameMapState] = useState<Map<string, string>>(new Map());
    // Store the boundary that was sent to the API so we can reuse it exactly for the output plot
    const [apiBoundary, setApiBoundary] = useState<[number, number][]>([]);
    const isHydratingRef = useRef(true);

    // Save functionality state
    const [documentTitle, setDocumentTitle] = useState(DEFAULT_NEW_INPUT_DOCUMENT_TITLE);
    const [savedDocumentId, setSavedDocumentId] = useState<string | null>(null);
    const [isSavingDocument, setIsSavingDocument] = useState(false);
    const initialRouteStateRef = useRef<any>(null);

    // Capture route state on mount (for reopening saved documents)
    useEffect(() => {
        const routeState = location.state as any;
        if (routeState?.newInputSavedData || routeState?.documentId) {
            initialRouteStateRef.current = routeState;
            if (routeState.documentTitle) setDocumentTitle(routeState.documentTitle);
            if (routeState.documentId) setSavedDocumentId(routeState.documentId);
        }
        // This runs once on mount to capture initial route state
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveStateToSession = useCallback((boundaryOverride?: [number, number][]) => {
        try {
            const lines = state.linesRef.current;
            const fallbackBoundary = convertLinesToBoundary(lines);
            const stateToSave = {
                rooms,
                fixedRooms,
                adjacencyPairs,
                nonAdjacencyPairs,
                gridSpacing,
                autoExpand,
                activeSection,
                apiBoundary: boundaryOverride ?? apiBoundary ?? fallbackBoundary,
                floorplans,
                currentFloorplanIndex,
                spaceResult,
                gaResult,
                idToNameMap: Object.fromEntries(idToNameMapState),
                canvasLines: lines,
                zoomTransform: state.zoomTransform.current,
            };
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(stateToSave));
        } catch (e) {
            console.warn("Failed to save input editor state:", e);
        }
    }, [
        rooms,
        fixedRooms,
        adjacencyPairs,
        nonAdjacencyPairs,
        gridSpacing,
        activeSection,
        apiBoundary,
        floorplans,
        currentFloorplanIndex,
        spaceResult,
        gaResult,
        idToNameMapState,
        autoExpand,
        state.linesRef,
        state.zoomTransform,
    ]);

    // ── Restore saved state when component mounts (back from output editor or from saved document) ──
    useEffect(() => {
        try {
            // First priority: Route state from saved document reopen
            const routeState = initialRouteStateRef.current;
            if (routeState?.newInputSavedData) {
                const savedData = routeState.newInputSavedData as EditorStateSnapshot;

                // Apply saved state
                if (savedData.rooms) setRooms(savedData.rooms);
                if (savedData.fixedRooms) setFixedRooms(savedData.fixedRooms);
                if (savedData.adjacencyPairs) setAdjacencyPairs(savedData.adjacencyPairs);
                if (savedData.nonAdjacencyPairs) setNonAdjacencyPairs(savedData.nonAdjacencyPairs);
                if (savedData.gridSpacing) setGridSpacing(savedData.gridSpacing);
                if (typeof savedData.autoExpand === "boolean") setAutoExpand(savedData.autoExpand);
                if (savedData.activeSection && ["boundary", "rooms", "adjacency", "nonadjacency"].includes(savedData.activeSection)) {
                    setActiveSection(savedData.activeSection as "boundary" | "rooms" | "adjacency" | "nonadjacency");
                }
                if (savedData.apiBoundary) setApiBoundary(savedData.apiBoundary);
                if (savedData.idToNameMap) setIdToNameMapState(new Map(Object.entries(savedData.idToNameMap)));
                if (savedData.floorplans) setFloorplans(savedData.floorplans);
                if (savedData.currentFloorplanIndex != null) setCurrentFloorplanIndex(savedData.currentFloorplanIndex);
                if (savedData.spaceResult) setSpaceResult(savedData.spaceResult);
                if (savedData.gaResult) setGAResult(savedData.gaResult);

                // Restore canvas lines after D3 groups are mounted
                if (savedData.canvasLines && savedData.canvasLines.length > 0) {
                    setTimeout(() => {
                        actions.restoreLines(savedData.canvasLines);
                        if (savedData.zoomTransform) {
                            actions.restoreZoom(savedData.zoomTransform);
                        }
                    }, 100);
                } else if (savedData.zoomTransform) {
                    setTimeout(() => {
                        actions.restoreZoom(savedData.zoomTransform);
                    }, 100);
                }
                setTimeout(() => {
                    isHydratingRef.current = false;
                }, 200);
                return;
            }

            // Second priority: Session storage (back from output editor)
            const raw = sessionStorage.getItem(SESSION_KEY);
            if (!raw) {
                isHydratingRef.current = false;
                return;
            }
            const saved = JSON.parse(raw);

            if (saved.rooms) setRooms(saved.rooms);
            if (saved.fixedRooms) setFixedRooms(saved.fixedRooms);
            if (saved.adjacencyPairs) setAdjacencyPairs(saved.adjacencyPairs);
            if (saved.nonAdjacencyPairs) setNonAdjacencyPairs(saved.nonAdjacencyPairs);
            if (saved.gridSpacing) setGridSpacing(saved.gridSpacing);
            if (typeof saved.autoExpand === "boolean") setAutoExpand(saved.autoExpand);
            if (saved.activeSection && ["boundary", "rooms", "adjacency", "nonadjacency"].includes(saved.activeSection)) {
                setActiveSection(saved.activeSection as "boundary" | "rooms" | "adjacency" | "nonadjacency");
            }
            if (saved.apiBoundary) setApiBoundary(saved.apiBoundary);
            if (saved.idToNameMap) setIdToNameMapState(new Map(Object.entries(saved.idToNameMap)));

            // Restore canvas lines after a short delay so D3 groups are mounted
            if (saved.canvasLines && saved.canvasLines.length > 0) {
                if (saved.floorplans) setFloorplans(saved.floorplans);
                if (saved.currentFloorplanIndex != null) setCurrentFloorplanIndex(saved.currentFloorplanIndex);
                if (saved.spaceResult) setSpaceResult(saved.spaceResult);
                if (saved.gaResult) setGAResult(saved.gaResult);
                setTimeout(() => {
                    actions.restoreLines(saved.canvasLines);
                    if (saved.zoomTransform) {
                        actions.restoreZoom(saved.zoomTransform);
                    }
                }, 100);
            } else if (saved.zoomTransform) {
                setTimeout(() => {
                    actions.restoreZoom(saved.zoomTransform);
                }, 100);
                sessionStorage.removeItem(SESSION_KEY);
            }
        } catch (e) {
            console.warn("Failed to restore input editor state:", e);
        } finally {
            setTimeout(() => {
                isHydratingRef.current = false;
            }, 200);
        }
        // Run only on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isHydratingRef.current) return;
        saveStateToSession();
    }, [saveStateToSession, state.revision]);

    useEffect(() => {
        if (hasValidBoundary) return;
        if (activeTool === "fixedspace") {
            setActiveTool(null);
        }
        if (pendingFixedRoom) {
            setPendingFixedRoom(null);
            setFixedRoomDialogOpen(false);
        }
    }, [hasValidBoundary, activeTool, pendingFixedRoom]);

    const handleSectionChange = (section: "boundary" | "rooms" | "adjacency" | "nonadjacency") => {
        // Toggle behavior if accordion? No, just set active.
        if (activeSection === section) {
            // Optional: collapse if same? But we want radio behavior probably.
            // Let's keep radio behavior for now.
        }

        if (section !== "rooms" && activeTool === "fixedspace") {
            setActiveTool(null);
        }

        setActiveSection(section);
        if (section === "boundary") {
            actions.setDrawing(true);
            actions.setDeleteMode(false);
        } else {
            actions.setDrawing(false);
        }
    };

    const handleToolChange = (tool: DrawingTool) => {
        if (tool === "fixedspace") {
            if (!hasValidBoundary) {
                setActiveTool(null);
                dispatch(showToast({
                    message: "Draw a valid closed boundary before adding fixed rooms.",
                    type: "warning",
                }));
                return;
            }

            setActiveTool("fixedspace");
            setActiveSection("rooms");
            actions.setDeleteMode(false);
            actions.setDrawing(false);
            dispatch(showToast({
                message: "Drag on canvas to draw a fixed room rectangle, then add details.",
                type: "warning",
            }));
            return;
        }

        setActiveTool(tool);

        if (activeSection === "boundary") {
            actions.setDeleteMode(false);
            actions.setDrawing(true);
        } else {
            actions.setDrawing(false);
        }
    };

    const nextFixedRoomName = useMemo(() => {
        const existingNames = new Set(fixedRooms.map((room) => room.name));
        let index = fixedRooms.length + 1;
        let candidate = `Fixed Room ${index}`;

        while (existingNames.has(candidate)) {
            index += 1;
            candidate = `Fixed Room ${index}`;
        }

        return candidate;
    }, [fixedRooms]);

    const handleStartFixedRoomDraw = () => {
        handleToolChange("fixedspace");
    };

    const handleCancelPendingFixedRoom = useCallback(() => {
        setPendingFixedRoom(null);
        setFixedRoomDialogOpen(false);
    }, []);

    const handleSavePendingFixedRoom = useCallback((details: { name: string; maxExpansion: number }) => {
        if (!pendingFixedRoom) return;

        const lines = state.linesRef.current;
        if (!isClosedRectilinearBoundary(lines)) {
            dispatch(showToast({ message: "Draw and close the floor boundary before adding fixed rooms", type: "error" }));
            return;
        }

        const boundary = convertLinesToBoundary(lines);
        const normalizedRoom = {
            x: Math.round(pendingFixedRoom.x),
            y: Math.round(pendingFixedRoom.y),
            width: Math.max(1, Math.round(pendingFixedRoom.width)),
            height: Math.max(1, Math.round(pendingFixedRoom.height)),
        };

        if (!isFixedRoomInsideBoundary(normalizedRoom, boundary)) {
            dispatch(showToast({
                message: "Fixed room must be fully inside the floor boundary",
                type: "error",
            }));
            return;
        }

        setFixedRooms((prev) => enforceUniqueEntityNames([
            ...prev,
            {
                id: `fixed-room-${Date.now()}-${Math.round(Math.random() * 10000)}`,
                name: details.name,
                x: normalizedRoom.x,
                y: normalizedRoom.y,
                width: normalizedRoom.width,
                height: normalizedRoom.height,
                maxExpansion: Math.max(0, details.maxExpansion || 0),
            },
        ]));

        setPendingFixedRoom(null);
        setFixedRoomDialogOpen(false);
        dispatch(showToast({ message: "Fixed room added successfully", type: "success" }));
    }, [pendingFixedRoom, state.linesRef, dispatch]);

    const handleFinalize = useCallback(() => {
        const lines = state.linesRef.current;
        if (!isClosedRectilinearBoundary(lines)) {
            dispatch(showToast({ message: "Draw a closed rectilinear boundary", type: "error" }));
            return;
        }

        const boundary = convertLinesToBoundary(lines);

        saveStateToSession(boundary);

        dispatch(showToast({ message: "Local state finalized and saved successfully", type: "success" }));
    }, [state.linesRef, dispatch, saveStateToSession]);

    const handleClear = useCallback(() => {
        actions.clearAll();
        actions.setDeleteMode(false);
        setActiveTool(null);
        setFixedRooms([]);
        setPendingFixedRoom(null);
        setFixedRoomDialogOpen(false);
        setFloorplans([]);
        setCurrentFloorplanIndex(0);
        setPreviewModalOpen(false);
        setSpaceResult(null);
        setGAResult(null);
        setApiBoundary([]);
        setIdToNameMapState(new Map());
        dispatch(resetStates());
        dispatch(reset());
        sessionStorage.removeItem(SESSION_KEY);
        dispatch(showToast({ message: "Cleared floorplan input and cached state", type: "success" }));
    }, [actions, dispatch]);

    // Helper for polling task results
    const pollTaskUtil = async (taskId: string) => {
        const pollInterval = 2000;
        const maxAttempts = 180; // 6 minutes max (GA takes 2-3 mins)
        let attempts = 0;

        while (attempts < maxAttempts) {
            const taskStatus = await fetchTaskResult(taskId);
            console.log(`Poll attempt ${attempts + 1}:`, taskStatus.status, taskStatus);

            // Handle both uppercase and lowercase status values from different API versions
            const status = taskStatus.status?.toLowerCase();

            if (status === "success" || status === "ok") {
                // Extract the actual result data
                return taskStatus.result || taskStatus.data || taskStatus;
            } else if (status === "failure" || status === "error") {
                throw new Error(extractErrorMessage(taskStatus, "Optimization task failed"));
            }
            // "pending" or "progress" - keep polling

            await new Promise((resolve) => setTimeout(resolve, pollInterval));
            attempts++;
        }
        throw new Error("Optimization timed out");
    };

    const handleGenerateFloorplan = useCallback(async (append = false) => {
        try {
            setLoading(true);
            setGAResult(null);
            if (!append) {
                setSpaceResult(null);
            }

            const lines = state.linesRef.current;
            if (!isClosedRectilinearBoundary(lines)) {
                dispatch(showToast({ message: "Draw a closed rectilinear boundary", type: "error" }));
                return;
            }

            const boundary = convertLinesToBoundary(lines);
            // Store boundary for later use in handleEditFloorplan
            setApiBoundary(boundary);

            if (pendingFixedRoom) {
                dispatch(showToast({ message: "Save or cancel the pending fixed room first", type: "warning" }));
                return;
            }

            const placeableRooms = rooms.filter((room) => room.name.trim().length > 0);

            if (placeableRooms.length === 0) {
                dispatch(showToast({ message: "Please add at least one room to generate a floorplan", type: "error" }));
                return;
            }

            const unnamedFixedRoom = fixedRooms.find((room) => room.name.trim().length === 0);
            if (unnamedFixedRoom) {
                dispatch(showToast({ message: "Please name all fixed rooms before generating", type: "error" }));
                return;
            }

            const boundaryBounds = getBoundaryBounds(boundary);
            const autoMaxExpansion = boundaryBounds
                ? Math.max(1, Math.round(Math.max(boundaryBounds.width, boundaryBounds.height)))
                : 0;

            const resolveMaxExpansion = (value: number) => {
                if (!autoExpand) return 0;
                const normalized = Math.max(0, Math.round(Number(value) || 0));
                if (normalized > 0) return normalized;
                return autoMaxExpansion;
            };

            const fixedRoomsPayload = fixedRooms.map((room) => ({
                name: room.name.trim(),
                x: Math.round(Number(room.x) || 0),
                y: Math.round(Number(room.y) || 0),
                width: Math.max(1, Math.round(Number(room.width) || 0)),
                height: Math.max(1, Math.round(Number(room.height) || 0)),
                is_fixed: true as const,
                max_expansion: 0,
            }));

            const outsideFixedRoom = fixedRoomsPayload.find((room) => !isFixedRoomInsideBoundary(room, boundary));
            if (outsideFixedRoom) {
                dispatch(showToast({
                    message: `Fixed room "${outsideFixedRoom.name}" is outside the floor boundary`,
                    type: "error",
                }));
                return;
            }

            // Room Name Mapping Logic
            const idToNameMap = new Map<string, string>();

            placeableRooms.forEach((r, index) => {
                const id = (index + 1).toString();
                idToNameMap.set(id, r.name);
            });

            // Store mapping for GA circulation step
            setIdToNameMapState(new Map(idToNameMap));

            const roomsPayload = placeableRooms.map((r) => ({
                // Space optimization API expects room names, not generated numeric IDs.
                name: r.name.trim(),
                width: Math.max(1, Math.round(Number(r.width) || 0)),
                height: Math.max(1, Math.round(Number(r.height) || 0)),
                max_expansion: resolveMaxExpansion(r.maxExpansion),
            }));

            const ops = autoExpand
                ? ["place", "compact", "expand", "score"]
                : ["place", "compact", "score"];

            const spacePayload = {
                request_id: `req-space-${uuidv4()}`,
                engine: "FloorPlan" as const,
                params: {
                    boundary: boundary,
                    rooms: roomsPayload,
                    adjacency: adjacencyPairs.map((p) => [p.room1, p.room2] as [string, string]),
                    non_adjacency: nonAdjacencyPairs.map((p) => [p.room1, p.room2] as [string, string]),
                    ...(fixedRoomsPayload.length > 0 ? { fixed_rooms: fixedRoomsPayload } : {}),
                    max_attempts: 1000,
                },
                ops: ops,
            };

            // Call Space Optimization only
            const spaceResponse = await fetchSpaceOptimization(spacePayload);

            if ('error' in spaceResponse && hasMeaningfulError((spaceResponse as any).error)) {
                throw new Error(extractErrorMessage(spaceResponse, "Space optimization request failed"));
            }

            let result: any;
            const immediateResponseData = (spaceResponse as any)?.data;
            const immediateOutput = immediateResponseData?.data || immediateResponseData;

            // New backend behavior may return completed output directly from the generation API.
            if (immediateOutput?.floor && immediateOutput?.placements) {
                result = immediateOutput;
            } else {
                const spaceTaskId = (spaceResponse as any).task_id || (spaceResponse as any).request_id;
                if (!spaceTaskId) throw new Error("No Task ID returned from Space Optimization");
                result = await pollTaskUtil(spaceTaskId);
            }
            console.log("Space optimization result:", result);

            // Handle nested data wrapping: result might be { data: { floor, placements } } or { floor, placements } directly
            const outputData = result?.data || result;

            if (!outputData || !outputData.floor || !outputData.placements) {
                throw new Error("Received invalid floorplan data from Space Optimization");
            }

            const normalizedOutputData = normalizePlacementsWithBoundaryShift(
                outputData,
                boundary,
                fixedRoomsPayload,
            );

            // Restore room names from input configuration (preserving backend placement width & height)
            if (normalizedOutputData.placements) {
                normalizedOutputData.placements = normalizedOutputData.placements.map((p: any) => {
                    const restoredName = idToNameMap.get(p.name) || p.name;
                    return {
                        ...p,
                        name: restoredName,
                    };
                });
            }

            const normalizedValidation = validateNormalizedFloorplan(
                normalizedOutputData,
                fixedRoomsPayload,
                boundary,
            );

            if (!normalizedValidation.isValid) {
                dispatch(showToast({
                    message: `Generated with geometry warnings: ${normalizedValidation.issues.slice(0, 2).join("; ")}`,
                    type: "warning",
                }));
            }

            setSpaceResult(result);
            if (append) {
                const nextFloorplans = [...floorplans, normalizedOutputData];
                setFloorplans(nextFloorplans);
                setCurrentFloorplanIndex(nextFloorplans.length - 1);
            } else {
                setFloorplans([normalizedOutputData]);
                setCurrentFloorplanIndex(0);
            }
            setPreviewModalOpen(true);
            dispatch(showToast({ message: "Floorplan generated successfully!", type: "success" }));

        } catch (err: any) {
            console.error(err);
            dispatch(showToast({ message: extractErrorMessage(err, "An error occurred"), type: "error" }));
        } finally {
            setLoading(false);
        }
    }, [state.linesRef, rooms, fixedRooms, pendingFixedRoom, adjacencyPairs, nonAdjacencyPairs, autoExpand, dispatch, floorplans]);

    // Generate Circulation Output (GA Optimization)
    const handleGenerateCirculation = useCallback(async () => {
        if (!spaceResult) {
            dispatch(showToast({ message: "No Space Optimization result available", type: "error" }));
            return;
        }

        try {
            setIsGALoading(true);

            // Re-create the space result with numeric names for GA input
            // The spaceResult was stored before name restoration, but we restored names in outputData
            // We need numeric names for GA. Let's re-map from the current floorplans.
            const currentOutput = floorplans[currentFloorplanIndex];
            if (!currentOutput || !currentOutput.placements) {
                throw new Error("No space optimization placements found");
            }

            // Convert Space Result to GA Payload
            const gaPayload = convertSpaceResultToGARequest({ data: currentOutput });

            // Call GA Optimization
            const gaResponse = await fetchGAOptimization(gaPayload);

            if ('error' in gaResponse && hasMeaningfulError((gaResponse as any).error)) {
                throw new Error(extractErrorMessage(gaResponse, "GA optimization request failed"));
            }

            const gaTaskId = (gaResponse as any).task_id;
            if (!gaTaskId) throw new Error("No Task ID returned from GA Optimization");

            const gaResultData = await pollTaskUtil(gaTaskId);
            const outputData = gaResultData?.data || gaResultData;

            setGAResult(outputData);
            setFloorplans([outputData]);
            setCurrentFloorplanIndex(0);
            dispatch(showToast({ message: "Circulation output generated successfully!", type: "success" }));

        } catch (err: any) {
            console.error(err);
            dispatch(showToast({ message: extractErrorMessage(err, "GA Optimization failed"), type: "error" }));
        } finally {
            setIsGALoading(false);
        }
    }, [spaceResult, floorplans, currentFloorplanIndex, dispatch]);

    const handleGenerateAnotherFloorplan = useCallback(async () => {
        await handleGenerateFloorplan(true);
    }, [handleGenerateFloorplan]);

    const areaStats = useMemo(() => {
        const revisionTick = state.revision;
        void revisionTick;
        const boundary = convertLinesToBoundary(state.linesRef.current);
        const totalArea = hasValidBoundary
            ? getPolygonArea(boundary)
            : 0;

        const roomArea = rooms.reduce((acc, room) => {
            const width = Number(room.width) || 0;
            const height = Number(room.height) || 0;
            return acc + width * height;
        }, 0) + fixedRooms.reduce((acc, room) => {
            const width = Number(room.width) || 0;
            const height = Number(room.height) || 0;
            return acc + width * height;
        }, 0);

        const remainingArea = totalArea - roomArea;
        const remainingPercentage = totalArea > 0 ? (remainingArea / totalArea) * 100 : 0;

        return {
            totalArea,
            roomArea,
            remainingArea,
            remainingPercentage,
            hasBoundary: hasValidBoundary,
        };
    }, [rooms, fixedRooms, state.linesRef, state.revision, hasValidBoundary]);

    const mappedTutorialSteps = useMemo(() => {
        return newInputEditorTourSteps.map((step) => {
            if (step.targetSelector === "[data-tour='new-input-draw-boundary']") {
                return {
                    ...step,
                    onEnter: () => {
                        handleSectionChange("boundary");
                    },
                };
            }
            if (step.targetSelector === "[data-tour='new-input-rooms']") {
                return {
                    ...step,
                    onEnter: () => {
                        handleSectionChange("rooms");
                    },
                };
            }
            if (step.targetSelector === "[data-tour='new-input-adjacency']") {
                return {
                    ...step,
                    onEnter: () => {
                        handleSectionChange("adjacency");
                    },
                };
            }
            if (step.targetSelector === "[data-tour='new-input-nonadjacency']") {
                return {
                    ...step,
                    onEnter: () => {
                        handleSectionChange("nonadjacency");
                    },
                };
            }
            return step;
        });
    }, [handleSectionChange]);

    const handleEditFloorplan = useCallback(() => {
        const outputData = floorplans[currentFloorplanIndex];
        if (!outputData) {
            dispatch(showToast({ message: "No floorplan data available", type: "error" }));
            return;
        }

        const isGAOutput = gaResult !== null;

        dispatch(resetStates());
        dispatch(reset());
        dispatch(setCleanUpDone(true));
        dispatch(setCleanUpRequired(false));
        dispatch(setFromNewInputEditor(true));

        // Determine floor dimensions — use the larger of region bounds and boundary bounds
        // so the scale factor encompasses the full boundary.
        const floor = outputData.floor;
        const floorRegionBounds = getRegionBounds(floor?.regions || []);
        const boundaryBounds = apiBoundary.length > 2 ? getBoundaryBounds(apiBoundary) : null;

        let floorWidth = floorRegionBounds?.width || floor?.width || 0;
        let floorHeight = floorRegionBounds?.height || floor?.height || 0;

        if (boundaryBounds) {
            floorWidth = Math.max(floorWidth, boundaryBounds.width);
            floorHeight = Math.max(floorHeight, boundaryBounds.height);
        }

        dispatch(setWidth(floorWidth));
        dispatch(setHeight(floorHeight));

        // Scale coordinates for the output editor using scaleCoordinate logic.
        // The API response is in real-world units. We need to convert to SVG pixel coordinates.
        const viewportWidth = window.visualViewport?.width || 1920;
        const viewportHeight = window.visualViewport?.height || 1080;
        const offsetX = viewportWidth * 0.3;
        const offsetY = viewportHeight * 0.3;
        const scaleFactor = floorHeight > 0 ? (viewportHeight * 0.5) / floorHeight : 10;

        const sc = (coord: number) => coord * scaleFactor;

        // Calculate bounds to normalize coordinates — include both placements/walls AND
        // the API boundary so that rooms and the plot boundary share the same origin.
        let minX = Infinity, minY = Infinity;
        if (isGAOutput && outputData.walls) {
            outputData.walls.forEach((w: any) => {
                minX = Math.min(minX, w.x1, w.x2);
                minY = Math.min(minY, w.y1, w.y2);
            });
        } else if (outputData.placements) {
            outputData.placements.forEach((p: any) => {
                minX = Math.min(minX, p.x);
                minY = Math.min(minY, p.y);
            });
        }

        // Also consider the boundary coordinates so the plot boundary aligns with rooms
        if (apiBoundary.length > 0) {
            apiBoundary.forEach(([bx, by]) => {
                minX = Math.min(minX, bx);
                minY = Math.min(minY, by);
            });
        }

        if (minX === Infinity) minX = 0;
        if (minY === Infinity) minY = 0;

        const scX = (coord: number) => (coord - minX) * scaleFactor + offsetX;
        const scY = (coord: number) => (coord - minY) * scaleFactor + offsetY;

        // GA output with explicit rooms/walls — scale to SVG pixels
        if (isGAOutput && outputData.walls && outputData.rooms) {
            const gaWalls = outputData.walls.map((w: any) => ({
                id: w.id || uuidv4(),
                x1: scX(w.x1), y1: scY(w.y1),
                x2: scX(w.x2), y2: scY(w.y2),
                roomIds: w.roomIds || [],
            }));

            const gaRooms = outputData.rooms.map((r: any) => ({
                id: r.id || r.name,
                name: r.name,
                color: getValidColor(r.color),
                walls: (r.walls || []).map((w: any) => ({
                    id: w.id || uuidv4(),
                    x1: scX(w.x1), y1: scY(w.y1),
                    x2: scX(w.x2), y2: scY(w.y2),
                    roomIds: w.roomIds || [r.id || r.name],
                })),
            }));

            const gaLabels = outputData.labels
                ? outputData.labels.map((l: any) => ({
                    x: scX(l.x), y: scY(l.y), text: l.text, roomId: l.roomId || l.text,
                }))
                : outputData.rooms.map((r: any) => {
                    let cx = 0, cy = 0;
                    if (r.walls && r.walls.length > 0) {
                        const xs = r.walls.flatMap((w: any) => [w.x1, w.x2]);
                        const ys = r.walls.flatMap((w: any) => [w.y1, w.y2]);
                        cx = (Math.min(...xs) + Math.max(...xs)) / 2;
                        cy = (Math.min(...ys) + Math.max(...ys)) / 2;
                    }
                    return { x: scX(cx), y: scY(cy), text: r.name, roomId: r.id || r.name };
                });

            dispatch(setRoomsState(gaRooms));
            dispatch(setWalls(gaWalls));
            dispatch(setLabels(gaLabels));
        }
        // Space Optimization output: build walls from placements, scale to SVG pixels
        else if (outputData.placements) {
            const newRooms: any[] = [];
            const newLabels: any[] = [];
            const newWalls: any[] = [];

            outputData.placements.forEach((p: any, index: number) => {
                const roomId = typeof p.id === "string" && p.id.trim().length > 0
                    ? p.id
                    : `room-${index + 1}-${uuidv4().slice(0, 8)}`;
                const sx = scX(p.x);
                const sy = scY(p.y);
                const sw = sc(p.width);
                const sh = sc(p.height);

                const roomWallCoords = [
                    { x1: sx, y1: sy, x2: sx + sw, y2: sy },
                    { x1: sx + sw, y1: sy, x2: sx + sw, y2: sy + sh },
                    { x1: sx + sw, y1: sy + sh, x2: sx, y2: sy + sh },
                    { x1: sx, y1: sy + sh, x2: sx, y2: sy },
                ];

                const mappedWalls = roomWallCoords.map((w) => ({
                    id: uuidv4(),
                    ...w,
                    roomId: roomId,
                    roomIds: [roomId],
                }));

                newWalls.push(...mappedWalls);
                newRooms.push({
                    id: roomId,
                    name: p.name,
                    color: getValidColor(undefined, index),
                    walls: mappedWalls,
                });
                newLabels.push({
                    x: sx + sw / 2,
                    y: sy + sh / 2,
                    text: p.name,
                    roomId: roomId,
                });
            });

            // ── Split and Merge Collinear Wall Segments ──
            // Where rooms share partial edges (e.g., a long wall of one room touches
            // two smaller rooms), we must split the long walls into minimal segments
            // and merge the overlapping parts so they correctly share roomIds.
            const MERGE_TOL = 5.0;
            const mergedWalls: typeof newWalls = [];

            const isHorizontal = (w: any) => Math.abs(w.y1 - w.y2) < MERGE_TOL;
            const isVertical = (w: any) => Math.abs(w.x1 - w.x2) < MERGE_TOL;

            const horizontalWalls = newWalls.filter(isHorizontal);
            const verticalWalls = newWalls.filter(isVertical);
            const diagonalWalls = newWalls.filter((w) => !isHorizontal(w) && !isVertical(w));

            mergedWalls.push(...diagonalWalls);

            // Process horizontal walls
            const hGroups = new Map<number, typeof newWalls>();
            horizontalWalls.forEach((w) => {
                const y = (w.y1 + w.y2) / 2;
                let groupY = y;
                for (const ky of Array.from(hGroups.keys())) {
                    if (Math.abs(ky - y) <= MERGE_TOL) {
                        groupY = ky;
                        break;
                    }
                }
                if (!hGroups.has(groupY)) hGroups.set(groupY, []);
                hGroups.get(groupY)!.push(w);
            });

            hGroups.forEach((group, yKey) => {
                const points: number[] = [];
                group.forEach((w) => {
                    points.push(w.x1, w.x2);
                });
                points.sort((a, b) => a - b);

                // Remove points that are too close to each other
                const uniquePoints: number[] = [];
                points.forEach((p) => {
                    if (uniquePoints.length === 0 || p - uniquePoints[uniquePoints.length - 1] > MERGE_TOL) {
                        uniquePoints.push(p);
                    }
                });

                for (let i = 0; i < uniquePoints.length - 1; i++) {
                    const p1 = uniquePoints[i];
                    const p2 = uniquePoints[i + 1];
                    const mid = (p1 + p2) / 2;

                    const coveringWalls = group.filter((w) => {
                        const minX = Math.min(w.x1, w.x2);
                        const maxX = Math.max(w.x1, w.x2);
                        return minX - MERGE_TOL <= mid && mid <= maxX + MERGE_TOL;
                    });

                    if (coveringWalls.length > 0) {
                        const roomIds = new Set<string>();
                        coveringWalls.forEach((w) => w.roomIds?.forEach((id: string) => roomIds.add(id)));
                        mergedWalls.push({
                            id: uuidv4(),
                            x1: p1,
                            y1: yKey,
                            x2: p2,
                            y2: yKey,
                            roomId: coveringWalls[0].roomId,
                            roomIds: Array.from(roomIds),
                        });
                    }
                }
            });

            // Process vertical walls
            const vGroups = new Map<number, typeof newWalls>();
            verticalWalls.forEach((w) => {
                const x = (w.x1 + w.x2) / 2;
                let groupX = x;
                for (const kx of Array.from(vGroups.keys())) {
                    if (Math.abs(kx - x) <= MERGE_TOL) {
                        groupX = kx;
                        break;
                    }
                }
                if (!vGroups.has(groupX)) vGroups.set(groupX, []);
                vGroups.get(groupX)!.push(w);
            });

            vGroups.forEach((group, xKey) => {
                const points: number[] = [];
                group.forEach((w) => {
                    points.push(w.y1, w.y2);
                });
                points.sort((a, b) => a - b);

                const uniquePoints: number[] = [];
                points.forEach((p) => {
                    if (uniquePoints.length === 0 || p - uniquePoints[uniquePoints.length - 1] > MERGE_TOL) {
                        uniquePoints.push(p);
                    }
                });

                for (let i = 0; i < uniquePoints.length - 1; i++) {
                    const p1 = uniquePoints[i];
                    const p2 = uniquePoints[i + 1];
                    const mid = (p1 + p2) / 2;

                    const coveringWalls = group.filter((w) => {
                        const minY = Math.min(w.y1, w.y2);
                        const maxY = Math.max(w.y1, w.y2);
                        return minY - MERGE_TOL <= mid && mid <= maxY + MERGE_TOL;
                    });

                    if (coveringWalls.length > 0) {
                        const roomIds = new Set<string>();
                        coveringWalls.forEach((w) => w.roomIds?.forEach((id: string) => roomIds.add(id)));
                        mergedWalls.push({
                            id: uuidv4(),
                            x1: xKey,
                            y1: p1,
                            x2: xKey,
                            y2: p2,
                            roomId: coveringWalls[0].roomId,
                            roomIds: Array.from(roomIds),
                        });
                    }
                }
            });

            // Update each room's wall references to point to the merged walls
            newRooms.forEach((room) => {
                room.walls = mergedWalls.filter(
                    (w) => w.roomIds?.includes(room.id),
                );
            });

            dispatch(setRoomsState(newRooms));
            dispatch(setWalls(mergedWalls));
            dispatch(setLabels(newLabels));
        }

        // Prefer original request boundary for plot rendering to preserve user orientation.
        // Fallback to floor.regions when request boundary is unavailable.
        if (apiBoundary.length > 2) {
            const plotWalls: any[] = [];
            for (let i = 0; i < apiBoundary.length; i++) {
                const [x1, y1] = apiBoundary[i];
                const [x2, y2] = apiBoundary[(i + 1) % apiBoundary.length];
                plotWalls.push({
                    id: uuidv4(),
                    roomIds: [],
                    x1: scX(x1),
                    y1: scY(y1),
                    x2: scX(x2),
                    y2: scY(y2),
                });
            }

            dispatch(setPlot(plotWalls));
            dispatch(setShowPlot(true));
        } else {
            const regions = outputData.floor?.regions;
            if (regions && regions.length > 0) {
                const plotWalls: any[] = [];
                regions.forEach((region: any) => {
                    const rx = region.x;
                    const ry = region.y;
                    const rw = region.width;
                    const rh = region.height;
                    // 4 walls per region rectangle, transformed through scX/scY
                    plotWalls.push(
                        { id: uuidv4(), roomIds: [], x1: scX(rx), y1: scY(ry), x2: scX(rx + rw), y2: scY(ry) },
                        { id: uuidv4(), roomIds: [], x1: scX(rx + rw), y1: scY(ry), x2: scX(rx + rw), y2: scY(ry + rh) },
                        { id: uuidv4(), roomIds: [], x1: scX(rx + rw), y1: scY(ry + rh), x2: scX(rx), y2: scY(ry + rh) },
                        { id: uuidv4(), roomIds: [], x1: scX(rx), y1: scY(ry + rh), x2: scX(rx), y2: scY(ry) },
                    );
                });
                dispatch(setPlot(plotWalls));
                // Plot Boundary defaults to off; user toggles it via the Tool Bar.
                dispatch(setShowPlot(true));
            }
        }

        saveStateToSession();

        setPreviewModalOpen(false);
        navigate("/outputeditor", {
            state: { originPath: "/newinputeditor" },
        });
    }, [floorplans, currentFloorplanIndex, gaResult, dispatch, navigate, saveStateToSession, apiBoundary, rooms]);

    const getSectionTitle = () => {
        if (activeTool === "fixedspace") return "Draw Fixed Room";

        switch (activeSection) {
            case "boundary": return "Draw Boundary";
            case "rooms": return "Configure Rooms";
            case "adjacency": return "Set Adjacency";
            case "nonadjacency": return "Set Non-Adjacency";
            default: return "Draw Boundary";
        }
    };

    // Save functionality helpers
    const getSaveValidationMessage = useCallback((): string | null => {
        // Validation: must have closed boundary
        if (!hasValidBoundary) {
            return "Draw a valid closed boundary first";
        }

        // Validation: no pending fixed room dialog
        if (fixedRoomDialogOpen || pendingFixedRoom) {
            return "Complete or cancel the pending fixed room";
        }

        // Validation: must have named rooms, fixed rooms, or floorplans
        const hasNamedRooms = rooms.length > 0 && rooms.some(r => r.name?.trim());
        const hasFixedRooms = fixedRooms.length > 0;
        const hasFloorplans = floorplans.length > 0;

        if (!hasNamedRooms && !hasFixedRooms && !hasFloorplans) {
            return "Add at least one named room, fixed room, or floorplan";
        }

        return null;
    }, [hasValidBoundary, fixedRoomDialogOpen, pendingFixedRoom, rooms, fixedRooms, floorplans]);

    const canSave = getSaveValidationMessage() === null;

    const buildSerializableEditorState = useCallback((): EditorStateSnapshot => {
        const lines = state.linesRef.current;
        const boundary = convertLinesToBoundary(lines);

        return {
            rooms,
            fixedRooms,
            adjacencyPairs,
            nonAdjacencyPairs,
            canvasLines: lines,
            zoomTransform: state.zoomTransform.current,
            gridSpacing,
            autoExpand,
            activeSection,
            floorplans,
            apiBoundary: apiBoundary.length > 0 ? apiBoundary : boundary,
            idToNameMap: Object.fromEntries(idToNameMapState),
        };
    }, [activeSection, adjacencyPairs, apiBoundary, fixedRooms, floorplans, gridSpacing, autoExpand, idToNameMapState, nonAdjacencyPairs, rooms, state.linesRef, state.zoomTransform]);

    const handleSaveDocument = useCallback(async () => {
        try {
            const errorMsg = getSaveValidationMessage();
            if (errorMsg) {
                dispatch(showToast({ message: errorMsg, type: "error" }));
                return;
            }

            setIsSavingDocument(true);
            const editorState = buildSerializableEditorState();
            const resolvedTitle = documentTitle.trim() || DEFAULT_NEW_INPUT_DOCUMENT_TITLE;

            if (resolvedTitle !== documentTitle) {
                setDocumentTitle(resolvedTitle);
            }

            const payload: NewInputDocumentPayload = buildNewInputDocumentPayload(resolvedTitle, editorState);

            let result;
            if (savedDocumentId) {
                // Update existing document
                result = await updateNewInputDocument(savedDocumentId, payload);
            } else {
                // Create new document
                result = await createNewInputDocument(payload);
            }

            if ("error" in result) {
                dispatch(showToast({
                    message: result.error || "Failed to save document",
                    type: "error"
                }));
            } else {
                // Extract ID from response
                const newDocumentId = result.id;
                setSavedDocumentId(newDocumentId);

                const actionMsg = savedDocumentId ? "Document updated" : "Document saved";
                dispatch(showToast({
                    message: actionMsg,
                    type: "success"
                }));
            }
        } catch (error) {
            console.error("Save document error:", error);
            dispatch(showToast({
                message: "Failed to save document",
                type: "error"
            }));
        } finally {
            setIsSavingDocument(false);
        }
    }, [documentTitle, savedDocumentId, getSaveValidationMessage, buildSerializableEditorState, dispatch]);

    const handleViewSavedDocuments = useCallback(() => {
        navigate("/saved-documents");
    }, [navigate]);

    return (
        <PageContainer>
            <SidebarWrapper data-tour="new-input-sidebar">
                <LeftNavigationSidebar
                    activeSection={activeSection}
                    onSectionChange={handleSectionChange}
                    onGenerateFloorplan={() => handleGenerateFloorplan(false)}
                    loading={loading}
                    rooms={rooms}
                    onRoomsChange={setRooms}
                    fixedRooms={fixedRooms}
                    onFixedRoomsChange={setFixedRooms}
                    onStartFixedRoomDraw={handleStartFixedRoomDraw}
                    isFixedRoomDrawMode={activeTool === "fixedspace"}
                    canUseFixedRoom={hasValidBoundary}
                    adjacencyPairs={adjacencyPairs}
                    onAdjacencyPairsChange={setAdjacencyPairs}
                    nonAdjacencyPairs={nonAdjacencyPairs}
                    onNonAdjacencyPairsChange={setNonAdjacencyPairs}
                    areaStats={areaStats}
                />
            </SidebarWrapper>

            <MainPanel>
                <SectionTitle>{getSectionTitle()}</SectionTitle>
                <div data-tour="new-input-toolbar">
                    <TopToolbar
                        documentTitle={documentTitle}
                        onDocumentTitleChange={setDocumentTitle}
                        gridSpacing={gridSpacing}
                        onGridSpacingChange={setGridSpacing}
                        activeTool={activeTool}
                        onToolChange={handleToolChange}
                        onClear={handleClear}
                        drawing={state.drawing}
                        onSelectDrawing={() => {
                            setActiveTool(null);
                            actions.setDeleteMode(false);
                            actions.setDrawing(true);
                        }}
                        onSelectMove={() => {
                            setActiveTool(null);
                            actions.setDeleteMode(false);
                            actions.setDrawing(false);
                        }}
                        deleteWallMode={state.deleteWallMode}
                        onToggleDeleteWallMode={() => {
                            setActiveTool(null);
                            actions.setDeleteMode(!state.deleteWallMode);
                        }}
                        onUndo={actions.undo}
                        onRedo={actions.redo}
                        canUndo={state.canUndo}
                        canRedo={state.canRedo}
                        canUseFixedRoom={hasValidBoundary}
                        onSave={handleSaveDocument}
                        isSaving={isSavingDocument}
                        canSave={canSave}
                        onViewSavedDocuments={handleViewSavedDocuments}
                        showViewSavedDocuments={true}
                        onFinalize={handleFinalize}
                        tutorialSteps={mappedTutorialSteps}
                        tutorialId="new-input-editor"
                        tutorialAutoStart={false}
                    />
                </div>

                <CanvasFrame data-tour="new-input-canvas">
                    <Canvas
                        svgRef={refs.svgRef}
                        gGridRef={refs.gGridRef}
                        gLinesRef={refs.gLinesRef}
                        gFixedRoomsRef={refs.gFixedRoomsRef}
                        gDimensionsRef={refs.gDimensionsRef}
                        gPointsRef={refs.gPointsRef}
                        gDeleteButtonsRef={refs.gDeleteButtonsRef}
                        drawing={state.drawing}
                        deleteWallMode={state.deleteWallMode}
                        fixedRoomMode={activeTool === "fixedspace"}
                    />
                    <div data-tour="new-input-zoom" style={{ position: "absolute", bottom: "16px", right: "16px" }}>
                        <ZoomControls
                            onZoomIn={() => actions.handleZoom(1.2)}
                            onZoomOut={() => actions.handleZoom(0.8)}
                            zoom={state.zoom}
                        />
                    </div>

                </CanvasFrame>

                {/* Rooms/Adjacency panels removed from here */}

            </MainPanel>

            <FloorplanPreviewModal
                open={previewModalOpen}
                onClose={() => setPreviewModalOpen(false)}
                floorplans={floorplans}
                currentIndex={currentFloorplanIndex}
                onIndexChange={setCurrentFloorplanIndex}
                onEditFloorplan={handleEditFloorplan}
                autoExpandEnabled={autoExpand}
                onAutoExpandChange={setAutoExpand}
                onGenerateCirculation={handleGenerateCirculation}
                onGenerateAnotherFloorplan={handleGenerateAnotherFloorplan}
                isGeneratingAnother={loading}
                isGALoading={isGALoading}
                hasGAResult={gaResult !== null}
                plotBoundary={apiBoundary.length > 2 ? apiBoundary : convertLinesToBoundary(state.linesRef.current)}
            />

            <FixedRoomDetailsDialog
                open={fixedRoomDialogOpen}
                draft={pendingFixedRoom}
                defaultName={nextFixedRoomName}
                onCancel={handleCancelPendingFixedRoom}
                onSave={handleSavePendingFixedRoom}
            />

        </PageContainer>
    );
};

export default BoundaryEditorPage;
